import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { useTrack } from 'utils/hooks/useMixPanel';

import { Button, Card, Heading, Paragraph, Reservation, ReservationCard } from '@expressable/syntax-ds';
import { ContentfulTherapistSummary } from 'components/ContentfulTherapistCard/ContentfulTherapistSummary';
import { useTranslation } from 'react-i18next';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { useSplitEvaluationCtxV2 } from '../SplitEvaluationsScheduleSectionV2';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { getReservationScheduleProps } from 'utils/date';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { routes } from 'router/routes';
import { useNavigate } from 'react-router-dom';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';

export const EvaluationSchedulePickTherapistV2 = () => {
  useAutoScrollToTop();
  useSignupNavigationGuard();
  const { commonTherapists, onSubmitTherapist, firstTimeSlot, secondTimeSlot } = useSplitEvaluationCtxV2();
  if (!commonTherapists) throw new Error('Expected common therapists to be set');
  if (!firstTimeSlot) throw new Error('Expected first time slot to be set');
  const { t } = useTranslation();
  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.SPLIT_PICK_THERAPIST);
  const { format } = useDisplayTimezone();

  const { filters } = useSignupDirectCtx();
  const navigate = useNavigate();
  if (!filters) throw new Error('Expected filters to be set');

  const therapistBadgesData = {
    clinicalAreas: filters.clinicalAreas,
    insuranceName:
      filters.paymentMethod.type === 'insurance' ? filters.paymentMethod.insuranceData.displayName : undefined,
    stateCode: filters.stateCode,
  };

  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.schedule.split.v2.therapist.title')}</Heading>
        <Paragraph size="lg">{t('directSignup.schedule.split.v2.therapist.subTitle')} </Paragraph>
      </div>

      <ReservationCard title={t('directSignup.schedule.split.v2.therapist.reservation.title')} className="w-full">
        <Reservation
          title={t('directSignup.schedule.split.v2.therapist.reservation.first.title')}
          ctaLabel={t('directSignup.schedule.split.v2.therapist.reservation.cta')}
          onCtaClick={() => navigate(routes.signupDirect.evaluation.schedule.split.v2.first)}
          {...getReservationScheduleProps({
            dateTime: firstTimeSlot.time,
            formatFn: format,
          })}
        />

        {secondTimeSlot && (
          <Reservation
            title={t('directSignup.schedule.split.v2.therapist.reservation.second.title')}
            ctaLabel={t('directSignup.schedule.split.v2.therapist.reservation.cta')}
            onCtaClick={() => navigate(routes.signupDirect.evaluation.schedule.split.v2.second)}
            {...getReservationScheduleProps({
              dateTime: secondTimeSlot.time,
              formatFn: format,
            })}
          />
        )}
      </ReservationCard>

      <div className="flex flex-col gap-e40 w-full">
        {commonTherapists.map(therapist => (
          <Card key={therapist.acuityCalendarID}>
            <ContentfulTherapistSummary
              {...therapistBadgesData}
              therapistName={`${therapist.firstName} ${therapist.lastName}`}
              shouldShowProfile={true}
              cta={
                <Button
                  onClick={() => {
                    onSubmitTherapist(therapist);
                  }}
                >
                  {t('directSignup.schedule.split.v2.therapist.cta')}
                </Button>
              }
            />
          </Card>
        ))}
      </div>
    </>
  );
};
